import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { style } from './active-filters.style.js'
import { HiddenFieldsMixin } from 'components/shared/hidden-fields/hidden-fields-mixin.js'
import { SelectedFilter } from 'components/discovery/plp/filters/plp-filters/typing/filters-typing.js'
import {
  FilterRemovedEvent,
  FiltersClearedEvent,
  PlpFiltersSelectedFiltersEvent,
} from 'components/discovery/plp/filters/plp-filters/events.js'

export const activeFiltersHiddenFields = ['filter-name'] as const

@customElement('csc-active-filters')
export class CSCActiveFilters extends HiddenFieldsMixin(LitElement, activeFiltersHiddenFields, { hiddenByDefault: ['filter-name'] }) {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: Array })
  filters: SelectedFilter[] = []

  /** @deprecated The filter name is shown unless `hidden-fields` contains `filter-name`. */
  @property({ type: Boolean, attribute: 'display-filter-name' })
  set displayFilterName(value: boolean) {
    this.setLegacyVisibility('display-filter-name', 'filter-name', value)
  }

  get displayFilterName() {
    return !this.isHidden('filter-name')
  }

  @property({ type: String, attribute: 'heading-variant' })
  headingVariant = 'heading-6'

  override connectedCallback(): void {
    super.connectedCallback()
    document.addEventListener(PlpFiltersSelectedFiltersEvent.type, this.handleSelectedFiltersUpdate)
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    document.removeEventListener(PlpFiltersSelectedFiltersEvent.type, this.handleSelectedFiltersUpdate)
  }

  private handleSelectedFiltersUpdate = (event: PlpFiltersSelectedFiltersEvent): void => {
    this.filters = event.selectedFilters
  }

  private handleFilterRemove = (event: CustomEvent) => {
    const chip = event.target as HTMLElement
    const index = parseInt(chip.dataset['index'] || '0', 10)

    const removedFilter = this.filters[index]
    if (removedFilter) {
      this.filters = this.filters.filter((_, i) => i !== index)
      this.dispatchEvent(new FilterRemovedEvent(removedFilter, index))
    }
  }

  private handleClearAll = () => {
    const clearedFilters = [...this.filters]
    this.filters = []
    this.dispatchEvent(new FiltersClearedEvent(clearedFilters))
  }

  private renderFilters = () => html`
    <cse-chip-group wrap class="filters-group">
      ${this.filters.map((filter, index) => html`
        <cse-chip
          variant="dismissable"
          data-url="${filter.api_link}"
          data-index="${index}"
          @cse-chip-dismissed=${this.handleFilterRemove}
        >
          ${!this.isHidden('filter-name') && filter.property_name && filter.property_value_name ?
            html`<strong>${filter.property_name}: </strong>${filter.property_value_name}` :
            filter.name}
        </cse-chip>
      `)}
    </cse-chip-group>
  `

  private renderDesktop = () => html`
    <div class="csc-active-filters desktop" >
      <cse-paper style="width: 100%">
        <cse-group column-gap="06" row-gap="06" alignment="center" class="filter-group">
          <cse-text variant="${this.headingVariant}" class="title">
            ${t('heading', { ns: 'csc-active-filters', default: 'Active filters' })}
          </cse-text>
          ${this.filters ? this.renderFilters() : nothing }
        </cse-group>
        <div class="link">
            <cse-link @click="${this.handleClearAll}" class="clear-btn">
              ${t('clear_selection_button', { ns: 'csc-active-filters', default: 'Clear selection' })}
            </cse-link>
        </div>
      </cse-paper>
    </div>
  `

  private renderMobile = () => html`
    <cse-group class="csc-active-filters mobile" direction="column" row-gap="06" justify="space-between">
      <cse-group column-gap="06" justify="space-between" alignment="center">
        <cse-text variant="${this.headingVariant}" class="title">
          ${t('heading', { ns: 'csc-active-filters', default: 'Active filters' })}
        </cse-text>
        <cse-link @click="${this.handleClearAll}">
          ${t('clear_selection_button', { ns: 'csc-active-filters', default: 'Clear selection' })}
        </cse-link>
      </cse-group>
      ${this.filters ? this.renderFilters() : nothing }
    </cse-group>
  `

  override render() {
    if (!this.filters.length) return nothing

    return html`
      ${this.renderDesktop()}
      ${this.renderMobile()}
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-active-filters': CSCActiveFilters
  }
}
